import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './auth';

export type Role =
  | 'Super Admin'
  | 'Admin'
  | 'Manager'
  | 'Agent'
  | 'Technician'
  | 'Viewer'
  | 'User'
  | 'Service Desk Agent'
  | 'Team Lead'
  | 'Department Head'
  | 'SOC Analyst'
  | 'Auditor'
  | 'Normal User';

export function normalizeRole(r?: string | null): Role {
  if (!r) return 'Normal User';
  const clean = r.trim();
  const lower = clean.toLowerCase();
  if (lower === 'super admin' || lower === 'superadmin') return 'Super Admin';
  if (lower === 'admin') return 'Admin';
  if (lower === 'manager') return 'Manager';
  if (lower === 'agent') return 'Agent';
  if (lower === 'technician') return 'Technician';
  if (lower === 'viewer') return 'Viewer';
  if (lower === 'user') return 'User';
  if (lower === 'normal user') return 'Normal User';
  if (lower === 'service desk agent') return 'Service Desk Agent';
  if (lower === 'team lead' || lower === 'team_lead') return 'Team Lead';
  if (lower === 'department head') return 'Department Head';
  if (lower === 'soc analyst' || lower === 'soc') return 'SOC Analyst';
  if (lower === 'auditor') return 'Auditor';
  return clean as Role;
}

type RoleContextValue = {
  role: Role | null;
  teamId: string | null;
  organizationId: string | null;
  loading: boolean;
  setRole: (r: Role) => void;
  setTeamId: (id: string | null) => void;
};

const RoleContext = createContext<RoleContextValue | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const { session, user, profile, loading: authLoading } = useAuth();
  const [role, setRole] = useState<Role | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!session && !user) {
      setRole('Super Admin'); // Fallback for testing/mock
      setTeamId(null);
      setOrganizationId(null);
      return;
    }

    // Resolve role from profile and user (already securely fetched via /api/profiles proxy in useAuth)
    const rawRole = profile?.role || user?.role || '';
    const assignedRole = normalizeRole(rawRole);
    const orgId = profile?.organization_id || user?.organizationId || null;
    const dbTeamId = profile?.team_id || (user?.teamIds?.[0] ?? null);

    setRole(assignedRole);
    setOrganizationId(orgId);
    setTeamId(dbTeamId);
  }, [session, user, profile, authLoading]);

  return (
    <RoleContext.Provider value={{ role, teamId, organizationId, loading: authLoading, setRole, setTeamId }}>
      {children}
    </RoleContext.Provider>
  );
}


// eslint-disable-next-line react-refresh/only-export-components
export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
